import { type FormEvent, useEffect, useState } from "react";
import {
  adminCreateIncident,
  adminCreateIncidentUpdate,
  adminDeleteIncident,
  adminUpdateIncident,
  fetchAdminIncidents,
  type IncidentCreatePayload,
  type IncidentUpdatePayload,
} from "../../lib/api";
import type {
  Incident,
  IncidentSeverity,
  IncidentStatus,
} from "@statuspage/shared";

const INCIDENT_STATI = ["investigating", "identified", "monitoring", "resolved"] as const;
const INCIDENT_SEVERITIES = ["minor", "major", "critical"] as const;

function isIncidentStatus(value: string): value is IncidentStatus {
  return INCIDENT_STATI.includes(value as IncidentStatus);
}

function isIncidentSeverity(value: string): value is IncidentSeverity {
  return INCIDENT_SEVERITIES.includes(value as IncidentSeverity);
}

const INITIAL_CREATE: IncidentCreatePayload = {
  title: "",
  status: "investigating",
  severity: "minor",
  body: null,
};

export function IncidentsPage(): React.ReactNode {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<IncidentCreatePayload>(INITIAL_CREATE);
  const [editIncidentId, setEditIncidentId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<IncidentStatus>("identified");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdminIncidents();
      setIncidents(response.incidents);
    } catch {
      setError("Incidents konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await adminCreateIncident(createForm);
      setCreateForm(INITIAL_CREATE);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Incident konnte nicht erstellt werden");
    }
  };

  const handleQuickUpdate = async (incidentId: string, status: IncidentStatus) => {
    const updates: IncidentUpdatePayload = {
      status,
    };
    try {
      await adminUpdateIncident(incidentId, updates);
      await adminCreateIncidentUpdate(incidentId, {
        status,
        body: `Status geändert auf ${status} via Dashboard`,
      });
      setEditIncidentId(null);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Update fehlgeschlagen");
    }
  };

  const handleDelete = async (incidentId: string) => {
    if (!confirm("Incident wirklich löschen?")) return;
    try {
      await adminDeleteIncident(incidentId);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    }
  };

  if (loading) {
    return <div className="px-6 py-8 dark:bg-slate-950 dark:text-slate-100">Lade Incidents…</div>;
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-8 dark:bg-slate-950 dark:text-slate-100">
      <h1 className="mb-4 text-2xl font-bold">Incidents</h1>
      {error && (
        <p className="mb-4 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}

      <form
        onSubmit={handleCreate}
        className="mb-6 grid gap-3 rounded border border-slate-200 bg-white p-4 sm:grid-cols-2 dark:border-slate-800 dark:bg-slate-900"
      >
        <label className="text-sm" htmlFor="incident-title">
          Titel
          <input
            id="incident-title"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            required
            value={createForm.title}
            onChange={(event) => setCreateForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <label className="text-sm" htmlFor="incident-status">
          Status
          <select
            id="incident-status"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={createForm.status}
            onChange={(event) => {
              const value = event.target.value;
              if (!isIncidentStatus(value)) {
                return;
              }
              setCreateForm((current) => ({ ...current, status: value }));
            }}
          >
            {INCIDENT_STATI.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm" htmlFor="incident-severity">
          Schweregrad
          <select
            id="incident-severity"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={createForm.severity}
            onChange={(event) => {
              const value = event.target.value;
              if (!isIncidentSeverity(value)) {
                return;
              }
              setCreateForm((current) => ({ ...current, severity: value }));
            }}
          >
            {INCIDENT_SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {severity}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm" htmlFor="incident-body">
          Beschreibung
          <input
            id="incident-body"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={createForm.body ?? ""}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, body: event.target.value.trim() === "" ? null : event.target.value }))
            }
          />
        </label>
        <div className="sm:col-span-2">
          <button
            type="submit"
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
          >
            Incident erstellen
          </button>
        </div>
      </form>

      <section className="space-y-3">
        {incidents.map((incident) => (
          <article
            key={incident.id}
            className="rounded border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold">{incident.title}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {incident.status} · {incident.severity}
                </p>
              </div>
              <button
                type="button"
                className="text-sm text-red-600 dark:text-red-400"
                onClick={() => handleDelete(incident.id)}
              >
                Löschen
              </button>
            </div>

            {editIncidentId === incident.id ? (
              <div className="mt-3 flex items-end gap-2">
                  <label className="text-sm" htmlFor={`incident-edit-status-${incident.id}`}>
                    Status
                    <select
                      id={`incident-edit-status-${incident.id}`}
                      className="mt-1 block rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      value={editStatus}
                      onChange={(event) => {
                        const value = event.target.value;
                      if (isIncidentStatus(value)) {
                        setEditStatus(value);
                      }
                    }}
                  >
                    {INCIDENT_STATI.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                    className="rounded bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400"
                  onClick={() => handleQuickUpdate(incident.id, editStatus)}
                >
                  Speichern
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-700"
                  onClick={() => setEditIncidentId(null)}
                >
                  Abbrechen
                </button>
              </div>
            ) : (
                <button
                  type="button"
                  className="mt-2 text-sm text-blue-600 dark:text-blue-400"
                  onClick={() => {
                    setEditIncidentId(incident.id);
                    setEditStatus(incident.status);
                }}
              >
                Status aktualisieren
              </button>
            )}
          </article>
        ))}
      </section>
    </main>
  );
}
